import { memo, useCallback } from 'react';
import Badge from '../Badge.jsx';
import Tooltip from '../Tooltip.jsx';
import { FILTERS_CHIP_BASE, FILTERS_CHIP_TRUNCATE } from './filtersTokens.js';
import { FILTERS_CHIP_MAX_CHARS } from './filtersTypes.js';

const stopPropagation = (e) => e.stopPropagation();

/**
 * Tag chip inside the filter field (Figma: Tag Component — informative, 24px, × 16px).
 * Labels &gt; {@link FILTERS_CHIP_MAX_CHARS} chars are truncated; full text in {@link Tooltip} (hover + focus).
 *
 * @param {object} props
 * @param {string} props.value
 * @param {string} props.label
 * @param {(value: string) => void} props.onRemove
 * @param {(label: string) => string} props.removeAriaLabel
 * @param {boolean} [props.disabled]
 */
const FilterChip = ({ value, label, onRemove, removeAriaLabel, disabled = false }) => {
  const needsTooltip = label.length > FILTERS_CHIP_MAX_CHARS;
  const displayLabel = needsTooltip ? `${label.slice(0, FILTERS_CHIP_MAX_CHARS)}…` : label;

  const handleClose = useCallback(() => onRemove(value), [onRemove, value]);

  const chip = (
    <span
      className="inline-flex shrink-0"
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <Badge
        variant="informative"
        size="medium"
        closable={!disabled}
        disabled={disabled}
        id={value}
        onClose={handleClose}
        closeButtonAriaLabel={removeAriaLabel(label)}
        className={FILTERS_CHIP_BASE}
      >
        <span className={FILTERS_CHIP_TRUNCATE}>{displayLabel}</span>
      </Badge>
    </span>
  );

  if (!needsTooltip) return chip;

  return (
    <Tooltip content={label} position="bottom" align="center" size="compact">
      {chip}
    </Tooltip>
  );
};

export default memo(FilterChip);
